import { Component } from '@theme/component';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * A custom element that manages a modal dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  /** @type {MediaQueryList | null} */
  #mediaQueryMin = null;
  /** @type {MediaQueryList | null} */
  #mediaQueryMax = null;
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#setupResponsiveListeners();

    // Listen to native dialog cancel event (e.g. Escape key)
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanupResponsiveListeners();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    unlockScroll(this.refs.dialog);
  }

  #setupResponsiveListeners() {
    if (this.minWidth) {
      this.#mediaQueryMin = window.matchMedia(`(max-width: ${this.minWidth - 1}px)`);
      this.#mediaQueryMin.addEventListener('change', this.#handleBreakpointChange);
    }
    if (this.maxWidth) {
      this.#mediaQueryMax = window.matchMedia(`(min-width: ${this.maxWidth + 1}px)`);
      this.#mediaQueryMax.addEventListener('change', this.#handleBreakpointChange);
    }
  }

  #cleanupResponsiveListeners() {
    this.#mediaQueryMin?.removeEventListener('change', this.#handleBreakpointChange);
    this.#mediaQueryMax?.removeEventListener('change', this.#handleBreakpointChange);
    this.#mediaQueryMin = null;
    this.#mediaQueryMax = null;
  }

  #handleBreakpointChange = (/** @type {{ matches: any; }} */ event) => {
    if (event.matches && this.refs.dialog.open) {
      this.closeDialog();
    }
  };

  /**
   * Native dialog cancel event (Escape key press)
   * @param {Event} event
   */
  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;
    if (dialog.open) return;

    this.#previousScrollY = getScrollTop();

    // Ensure state changes execute in a clean frame
    requestAnimationFrame(() => {
      lockScroll(dialog);
      dialog.showModal();

      dialog.addEventListener('click', this.#handleClick);
      this.dispatchEvent(new DialogOpenEvent());
    });
  }

  /**
   * Closes the dialog with exit animation support.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;
    if (!dialog.open) return;

    dialog.removeEventListener('click', this.#handleClick);

    // Apply closing class for exit keyframe/transition CSS without forcing layout reflow
    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, { subtree: false }).catch(() => {
      // Fallback safeguard if animation fails or is omitted
    });

    unlockScroll(dialog);
    if (this.#previousScrollY > 0) {
      scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog state.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Handles click events on the dialog backdrop or outer container.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;
    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Gets the minimum width threshold of the dialog.
   * @returns {number}
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width')) || 0;
  }

  /**
   * Gets the maximum width threshold of the dialog.
   * @returns {number}
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width')) || 0;
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true });
  }
  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName, { bubbles: true });
  }
  static eventName = 'dialog:close';
}

// Global listener for details element scroll-lock toggling
document.addEventListener(
  'toggle',
  (event) => {
    const { target } = event;
    if (target instanceof HTMLDetailsElement && target.hasAttribute('scroll-lock')) {
      if (target.open) {
        lockScroll(target);
      } else {
        unlockScroll(target);
      }
    }
  },
  { capture: true }
);